import { ReactNode } from "react";
import { ExternalLink, MessageCircleQuestion, Stethoscope, User, Users, Zap } from "lucide-react";
import yn from "yn";
import { AirflowAppIcon, SnowflakeAppIcon } from "~/assets/icons";
import useUserPermission from "~/hooks/useUserPermission";
import {
  SectionNavLink,
  sectionNavLinkClassName,
} from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import {
  nextgenAdminGroupsPath,
  nextgenAdminQuestionInsightsPath,
  nextgenAdminQuickActionsPath,
  nextgenAdminTurnDiagnosticsPath,
  nextgenAdminUsersPath,
} from "~/pages/manifold-nextgen/paths";

const { MODE, VITE_CUSTOMER } = import.meta.env;

interface AdminExternalNavLinkProps {
  href: string;
  icon: ReactNode;
  label: string;
}

// Mirrors the legacy NavMenu's Airflow/Snowflake link-outs (NavMenu.tsx), which
// leave the SPA entirely — a plain anchor, not a client-side NavLink.
const AdminExternalNavLink = ({ href, icon, label }: AdminExternalNavLinkProps) => (
  <li>
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={sectionNavLinkClassName({ isActive: false }, "px-3 py-2")}
    >
      {/* These app icons ship with a grayscale/brighten filter baked into the
          SVG (tuned for the legacy dark nav rail). brightness-0 crushes that
          to solid black regardless of the baked-in filter, dark:invert flips
          it to white in dark mode, and opacity-60 matches the muted-foreground
          weight of the User/Users icons above. */}
      <span className="shrink-0 opacity-60 brightness-0 dark:invert">{icon}</span>
      <span className="flex-1">{label}</span>
      <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
    </a>
  </li>
);

export const AdminSidebar = () => {
  const canManageQuickActions = useUserPermission("manage:quick-actions");
  const canUseAirflow = useUserPermission("user:airflow");
  const canViewSnowflake = useUserPermission("view:snowflake-ui");
  // N.B. read here rather than at module scope so tests can stub the flag.
  const canViewTurnDiagnostics =
    useUserPermission("view:all-ai-conversations") &&
    yn(import.meta.env.VITE_CONVERSATION_DIAGNOSTICS_ENABLED, { default: false });
  const questionInsightsEnabled = yn(import.meta.env.VITE_QUESTION_INSIGHTS_ENABLED, {
    default: false,
  });

  // Same hostname/database derivation as the legacy NavMenu (NavMenu.tsx) so
  // the link-outs land on the same Airflow/Snowflake instance.
  const hostname = window.location.hostname.split(".").slice(-4).join(".");
  const databaseName = MODE === "production" ? `${VITE_CUSTOMER}_prod` : `${VITE_CUSTOMER}_stage`;

  return (
    <SectionSidebar ariaLabel="Admin navigation">
      <ul className="flex flex-col gap-0.5 px-4 pt-4">
        <li>
          <SectionNavLink
            to={nextgenAdminUsersPath()}
            icon={<User className="size-4" />}
            label="Users"
          />
        </li>
        <li>
          <SectionNavLink
            to={nextgenAdminGroupsPath()}
            icon={<Users className="size-4" />}
            label="Groups"
          />
        </li>
        {canManageQuickActions && (
          <li>
            <SectionNavLink
              to={nextgenAdminQuickActionsPath()}
              icon={<Zap className="size-4" />}
              label="Quick Actions"
            />
          </li>
        )}
        {canViewTurnDiagnostics && (
          <li>
            <SectionNavLink
              to={nextgenAdminTurnDiagnosticsPath()}
              icon={<Stethoscope className="size-4" />}
              label="Turn Diagnostics"
            />
          </li>
        )}
        {questionInsightsEnabled && (
          <li>
            <SectionNavLink
              to={nextgenAdminQuestionInsightsPath()}
              icon={<MessageCircleQuestion className="size-4" />}
              label="AI Session Query Insights"
            />
          </li>
        )}
        {canUseAirflow && (
          <AdminExternalNavLink
            href={`https://airflow.${hostname}`}
            icon={<AirflowAppIcon className="size-4" />}
            label="Airflow"
          />
        )}
        {canViewSnowflake && (
          <AdminExternalNavLink
            href={`https://app.snowflake.com/sciencecloud/${VITE_CUSTOMER}/#/data/databases/${databaseName.toUpperCase()}`}
            icon={<SnowflakeAppIcon className="size-4" />}
            label="Snowflake"
          />
        )}
      </ul>
    </SectionSidebar>
  );
};
